// @hyphesync/shared — Zod schemas and types shared by apps/api and the SPAs.
//
// Consumed as TypeScript SOURCE everywhere (package.json `exports` → ./src/index.ts, and
// tsconfig.base.json `paths`). There is deliberately no build step and no dist/: SunshineBids
// resolved its shared package through a gitignored dist in tsc but through src in vitest/vite,
// and a stale dist made the typecheck lie (its S264). One specifier, one file.
import { z } from "zod";

/** Every provider a connector can speak (plan §5). AEMP ships disabled in M1 (D7). */
export const PROVIDERS = ["geotab", "vermeerone", "maintainx", "aemp"] as const;
export const ProviderSchema = z.enum(PROVIDERS);
export type Provider = z.infer<typeof ProviderSchema>;

export const ENVIRONMENTS = ["dev", "prod"] as const;
export type Environment = (typeof ENVIRONMENTS)[number];

/** GET /api/health — shallow liveness; touches no binding, carries no tenant data. */
export const HealthResponseSchema = z.object({
  ok: z.literal(true),
  service: z.literal("hyphesync-api"),
  environment: z.enum(ENVIRONMENTS),
  version: z.string().max(32),
});
export type HealthResponse = z.infer<typeof HealthResponseSchema>;

// ── Identity (M1.1, plan §7) ───────────────────────────────────────────────────────────────
/** Roles stored per user. `super_admin` is never stored: SUPER_ADMIN_EMAILS confers it. */
export const TENANT_ROLES = ["admin", "manager", "viewer"] as const;
export const TenantRoleSchema = z.enum(TENANT_ROLES);
export type TenantRole = z.infer<typeof TenantRoleSchema>;

export const USER_STATUSES = ["active", "disabled"] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

/** GET /api/me */
export const MeResponseSchema = z.object({
  user: z.object({
    id: z.string(),
    email: z.string(),
    name: z.string(),
    role: TenantRoleSchema,
    isSuperAdmin: z.boolean(),
  }),
  tenant: z.object({ id: z.string(), name: z.string(), slug: z.string(), timezone: z.string() }),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

export const UserSummarySchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  role: TenantRoleSchema,
  status: z.enum(USER_STATUSES),
  createdAt: z.string(),
  lastSeenAt: z.string().nullable(),
});
export type UserSummary = z.infer<typeof UserSummarySchema>;

export const InvitationSummarySchema = z.object({
  id: z.string(),
  email: z.string(),
  role: TenantRoleSchema,
  expiresAt: z.string(),
  createdAt: z.string(),
});
export type InvitationSummary = z.infer<typeof InvitationSummarySchema>;

/** Emails are compared lower-cased and trimmed everywhere; normalise once, at the edge. */
const EmailSchema = z.string().trim().toLowerCase().email().max(254);

/** POST /api/invitations */
export const CreateInvitationRequestSchema = z.object({ email: EmailSchema, role: TenantRoleSchema });
export type CreateInvitationRequest = z.infer<typeof CreateInvitationRequestSchema>;
export const CreateInvitationResponseSchema = z.object({
  invitation: InvitationSummarySchema,
  /** Contains the one-time token: shown to the admin once, never stored or listed again. */
  inviteUrl: z.string(),
  emailSent: z.boolean(),
});
export type CreateInvitationResponse = z.infer<typeof CreateInvitationResponseSchema>;

/** PATCH /api/users/:id (at least one field). */
export const UpdateUserRequestSchema = z
  .object({ role: TenantRoleSchema.optional(), status: z.enum(USER_STATUSES).optional() })
  .refine((v) => v.role !== undefined || v.status !== undefined, "Nothing to update");
export type UpdateUserRequest = z.infer<typeof UpdateUserRequestSchema>;

/** POST /api/invitations/accept. The token is 32 random bytes, base64url (43 chars). */
export const AcceptInvitationRequestSchema = z.object({ token: z.string().regex(/^[A-Za-z0-9_-]{43}$/) });
export type AcceptInvitationRequest = z.infer<typeof AcceptInvitationRequestSchema>;

/** Every `error` code the API answers when a signed-in person can't get in or can't join. The
 *  API's deny() only accepts these, and the SPA has a screen for each, so adding one here is the
 *  only way to add one anywhere. */
export const ACCESS_ERRORS = [
  "no_access", "disabled", "tenant_suspended", "org_mismatch",
  // invitation accept only
  "email_missing", "email_unverified", "email_mismatch",
  "invitation_expired", "invitation_invalid", "already_member", "already_accepted",
] as const;
export type AccessError = (typeof ACCESS_ERRORS)[number];

/** Every `error` code an admin action (Users page) can be refused with, besides the access codes.
 *  The API's fail() only accepts these, and the SPA's describeError() must word each one, so a
 *  new or renamed code fails typecheck on both sides instead of reaching users as "Something
 *  went wrong". */
export const ACTION_ERRORS = [
  "last_admin", "cannot_modify_self", "protected_user", "invite_limit",
  // connectors (M1.2)
  "connector_name_taken", "provider_unavailable", "credentials_missing", "credentials_unavailable",
  "connector_changed", "connector_inactive", "run_in_progress", "test_cooldown",
] as const;
export type ActionError = (typeof ACTION_ERRORS)[number];

// ── Connectors (M1.2, plan §4.2 / §5, D8, OD-33) ───────────────────────────────────────────
/** Every provider a connector row may name: the real ones plus the dev-only `demo` exit-check
 *  connector (OD-33), which the API refuses to create outside ENVIRONMENT = dev. */
export const CONNECTOR_PROVIDERS = [...PROVIDERS, "demo"] as const;
export const ConnectorProviderSchema = z.enum(CONNECTOR_PROVIDERS);
export type ConnectorProvider = z.infer<typeof ConnectorProviderSchema>;

export const CONNECTOR_STATUSES = ["active", "needs_attention"] as const;
export type ConnectorStatus = (typeof CONNECTOR_STATUSES)[number];

/** Structured failure classes (plan §5 http.ts), plus `internal` for our own bugs. */
export const ERROR_CLASSES = ["auth", "rate_limit", "upstream", "schema", "internal"] as const;
export type ErrorClass = (typeof ERROR_CLASSES)[number];

/** 5 min (the dispatcher's tick) to 24 h, in whole ticks: the scheduler runs on the 5-min slot
 *  grid (apps/api/src/scheduler/slots.ts), so any other value would silently run at the next
 *  multiple of 5 min while showing the configured one (420 s would run every 10 min). */
export const CadenceSecsSchema = z.number().int().min(300).max(86400).multipleOf(300);

/** demo: synthetic data. `slowMs` holds the lease long enough to watch overlap prevention;
 *  `failWith` exercises the failure paths (auth pause, backoff, alerts) on dev. */
export const DemoConfigSchema = z.object({
  /** Stands in for a real provider's account identity (Geotab database, MaintainX org). */
  account: z.string().trim().min(1).max(40).default("demo"),
  units: z.number().int().min(1).max(50).default(5),
  slowMs: z.number().int().min(0).max(600_000).default(0),
  failWith: z.enum(["none", "auth", "upstream"]).default("none"),
}).strict();
export const DemoCredentialsSchema = z.object({ secret: z.string().min(1).max(200) }).strict();

/** Per-provider config + credential schemas. Only providers with a connector implementation
 *  are listed; creating any other is refused with `provider_unavailable` until its slice lands
 *  (MaintainX M1.3, Geotab M1.4, VermeerOne + AEMP M1.5).
 *
 *  `accountKeys` are the config fields that say WHICH upstream account the connector reads
 *  (e.g. Geotab server + database). Changing one, or setting new credentials, resets the stored
 *  cursor, so a re-pointed connector never resumes from another account's position. */
export const CONNECTOR_SCHEMAS = {
  demo: { config: DemoConfigSchema, credentials: DemoCredentialsSchema, accountKeys: ["account"] },
} as const satisfies Partial<Record<ConnectorProvider, {
  config: z.ZodTypeAny; credentials: z.ZodTypeAny; accountKeys: readonly string[];
}>>;
export type ImplementedProvider = keyof typeof CONNECTOR_SCHEMAS;

/** What the API returns for a connector. Credentials are WRITE-ONLY (D8): only whether they are
 *  set and when they were last rotated ever leave the API. */
export const ConnectorSummarySchema = z.object({
  id: z.string(),
  provider: ConnectorProviderSchema,
  name: z.string(),
  enabled: z.boolean(),
  status: z.enum(CONNECTOR_STATUSES),
  config: z.record(z.string(), z.unknown()),
  hasCredentials: z.boolean(),
  credLastRotatedAt: z.string().nullable(),
  cadenceSecs: z.number(),
  nextRunAt: z.string().nullable(),
  lastRunAt: z.string().nullable(),
  lastSuccessAt: z.string().nullable(),
  consecutiveFailures: z.number(),
  lastErrorClass: z.enum(ERROR_CLASSES).nullable(),
  lastError: z.string().nullable(),
  createdAt: z.string(),
  /** The admin-edit version. Send it back as PATCH `ifUpdatedAt` so an edit made from a stale
   *  screen answers 409 `connector_changed` instead of reverting someone else's change. */
  updatedAt: z.string(),
});
export type ConnectorSummary = z.infer<typeof ConnectorSummarySchema>;

/** No control (\p{Cc}) or invisible format (\p{Cf}: bidi overrides, zero-width) characters: the
 *  name goes into alert email subjects and every admin screen, where those spoof or break it. */
const ConnectorNameSchema = z.string().trim().min(1).max(80).regex(/^[^\p{Cc}\p{Cf}]+$/u, "No control or invisible characters");

/** POST /api/connectors. `config` is validated against the provider's schema by the API. */
export const CreateConnectorRequestSchema = z.object({
  provider: ConnectorProviderSchema,
  name: ConnectorNameSchema,
  config: z.record(z.string(), z.unknown()).default({}),
  cadenceSecs: CadenceSecsSchema.default(300),
}).strict();
export type CreateConnectorRequest = z.input<typeof CreateConnectorRequestSchema>;

/** PATCH /api/connectors/:id. A connector paused by auth failures (OD-33) resumes only by
 *  `resume: true`, by being turned on from off, or by new credentials; re-sending
 *  `enabled: true` with an unrelated edit never lifts the pause. `resume: true` on a connector
 *  that is on and not paused changes nothing (it is not a "run now"). Only the fields sent are
 *  written, and an edit that raced another admin change answers 409 `connector_changed`. */
export const UpdateConnectorRequestSchema = z.object({
  name: ConnectorNameSchema.optional(),
  config: z.record(z.string(), z.unknown()).optional(),
  cadenceSecs: CadenceSecsSchema.optional(),
  enabled: z.boolean().optional(),
  resume: z.literal(true).optional(),
  /** Optional version check: the `updatedAt` the edit was made from. */
  ifUpdatedAt: z.string().max(40).optional(),
}).strict().refine(
  (v) => Object.entries(v).some(([k, x]) => k !== "ifUpdatedAt" && x !== undefined),
  "Nothing to update",
);
export type UpdateConnectorRequest = z.infer<typeof UpdateConnectorRequestSchema>;

/** PUT /api/connectors/:id/credentials. Validated against the provider's credential schema. */
export const SetCredentialsRequestSchema = z.object({ credentials: z.record(z.string(), z.unknown()) }).strict();
export type SetCredentialsRequest = z.infer<typeof SetCredentialsRequestSchema>;

/** POST /api/connectors/:id/test: a cheap authenticated call. `message` never carries a secret. */
export const ConnectorTestResponseSchema = z.object({ ok: z.boolean(), message: z.string() });
export type ConnectorTestResponse = z.infer<typeof ConnectorTestResponseSchema>;

/** POST /api/connectors/:id/run-now (202): the run is queued; it shows on Sync health. */
export const RunNowResponseSchema = z.object({ runId: z.string() });
export type RunNowResponse = z.infer<typeof RunNowResponseSchema>;

// ── Sync health (M1.2 part 3, plan §8 row 3, OD-33, OD-34) ─────────────────────────────────
export const RUN_STATUSES = ["queued", "running", "ok", "error", "skipped"] as const;
export type RunStatus = (typeof RUN_STATUSES)[number];
export const RUN_TRIGGERS = ["schedule", "manual"] as const;
export type RunTrigger = (typeof RUN_TRIGGERS)[number];

/** One connector's health, decided by the API (the backend is the only calculation authority),
 *  in this order of precedence:
 *  `off` (switched off) · `paused` (needs_attention after auth failures, OD-33) · `failing` (the
 *  last run failed and is part of a failure streak) · `late` (on and due, but not dispatched for
 *  over LATE_AFTER_SECS: the scheduler itself is in trouble) · `awaiting_run` (the last run failed,
 *  but an admin has since reset the streak: new credentials or Resume; the next run will tell) ·
 *  `never_run` · `healthy`. */
export const CONNECTOR_HEALTH = ["off", "paused", "failing", "late", "awaiting_run", "never_run", "healthy"] as const;
export type ConnectorHealth = (typeof CONNECTOR_HEALTH)[number];
/** Two missed 5-minute dispatcher ticks. */
export const LATE_AFTER_SECS = 600;

/** Runs shown per page (inline on Sync health, and each "Load older" page). */
export const RUNS_PAGE_SIZE = 20;

export const ConnectorRunSchema = z.object({
  id: z.string(),
  trigger: z.enum(RUN_TRIGGERS),
  status: z.enum(RUN_STATUSES),
  queuedAt: z.string(),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
  /** finishedAt − startedAt, computed by the API; null until the run has both. */
  durationMs: z.number().nullable(),
  /** What the run ingested, e.g. { units: 5, rawRecords: 1 }. Empty for a run with none. */
  counts: z.record(z.string(), z.number()),
  errorClass: z.enum(ERROR_CLASSES).nullable(),
  /** Why a run failed or was skipped. Never carries a secret (connectors/shared/http.ts). */
  message: z.string().nullable(),
});
export type ConnectorRun = z.infer<typeof ConnectorRunSchema>;

export const RunTotalsSchema = z.object({ ok: z.number(), error: z.number(), skipped: z.number() });
export type RunTotals = z.infer<typeof RunTotalsSchema>;

/** Per connector on GET /api/sync-health. Read-only, for admin + manager (OD-33, OD-34): only
 *  WHETHER credentials are set and when they last rotated, never anything of them (D8). */
export const ConnectorHealthSchema = z.object({
  id: z.string(),
  name: z.string(),
  provider: ConnectorProviderSchema,
  enabled: z.boolean(),
  status: z.enum(CONNECTOR_STATUSES),
  health: z.enum(CONNECTOR_HEALTH),
  /** A run holds the connector's lease right now. */
  running: z.boolean(),
  hasCredentials: z.boolean(),
  credLastRotatedAt: z.string().nullable(),
  cadenceSecs: z.number(),
  nextRunAt: z.string().nullable(),
  lastRunAt: z.string().nullable(),
  lastSuccessAt: z.string().nullable(),
  consecutiveFailures: z.number(),
  lastErrorClass: z.enum(ERROR_CLASSES).nullable(),
  lastError: z.string().nullable(),
  /** When the current failure streak began; null when not failing. */
  failingSince: z.string().nullable(),
  /** The admin-edit version (as on ConnectorSummary), for Resume's `ifUpdatedAt`. */
  updatedAt: z.string(),
  /** Finished runs by outcome over the last 24 h. */
  last24h: RunTotalsSchema,
  /** The newest RUNS_PAGE_SIZE runs, newest first. */
  runs: z.array(ConnectorRunSchema),
  /** Cursor for GET /api/sync-health/:id/runs?before=…; null when there are no older runs. */
  olderRunsCursor: z.string().nullable(),
});
export type ConnectorHealthSummary = z.infer<typeof ConnectorHealthSchema>;

/** GET /api/sync-health */
export const SyncHealthResponseSchema = z.object({
  /** Server time the answer was computed at: "x min ago" is measured from this, not the
   *  browser's clock. */
  generatedAt: z.string(),
  connectors: z.array(ConnectorHealthSchema),
  /** Super admins only (OD-34): the shared D1's size, which spans every tenant (plan §6's
   *  per-tenant-D1 trigger is 5 GB). Absent for everyone else; sizeBytes null when D1 didn't
   *  report it. */
  database: z.object({ sizeBytes: z.number().nullable() }).optional(),
});
export type SyncHealthResponse = z.infer<typeof SyncHealthResponseSchema>;

/** GET /api/sync-health/:connectorId/runs?before=<cursor> */
export const ConnectorRunsPageSchema = z.object({
  runs: z.array(ConnectorRunSchema),
  olderRunsCursor: z.string().nullable(),
});
export type ConnectorRunsPage = z.infer<typeof ConnectorRunsPageSchema>;

/** GET /api/connectors */
export const ConnectorListResponseSchema = z.object({
  connectors: z.array(ConnectorSummarySchema),
  /** Providers an admin may add HERE (implemented, and `demo` only on dev). The API decides. */
  availableProviders: z.array(ConnectorProviderSchema),
});
export type ConnectorListResponse = z.infer<typeof ConnectorListResponseSchema>;
